import { API_HOST } from '../config'
import { fetchWithTimeout } from '../support'
import type { ApiEnvelope } from '../types'
import type { ChatModerationState } from './types'

/**
 * The chat endpoints — the DURABLE half of chat. None of these are on the send
 * path: messages appear in the room over the data channel whether or not any of
 * this succeeds. History is what a late joiner and a reconnecting tutor read;
 * persistence is the safeguarding audit trail; moderation mints the rev every
 * client orders freeze/mute state by.
 */

const HISTORY_PATH = '/api/v1/public/livekit/chat_history'
const MESSAGES_PATH = '/api/v1/public/livekit/chat_messages'
const MODERATION_PATH = '/api/v1/public/livekit/chat_moderation'

const TIMEOUT_MS = 10_000

export type Credential = { code?: string | null; reconnectToken?: string | null }

export interface HistoryResult {
  state: ChatModerationState
  /** Ids deleted by a teacher — includes any whose rows aged out of the window. */
  tombstones?: string[]
  messages: Array<{
    id: string
    identity: string
    name: string | null
    at: number
    body: string | null
    attachment?: unknown
    deleted: boolean
  }>
}

function credentialBody(credential: Credential) {
  return {
    code: credential.code ?? undefined,
    reconnect_token: credential.reconnectToken ?? undefined,
  }
}

async function post<T>(path: string, body: object, keepalive = false): Promise<T> {
  const response = await fetchWithTimeout(`${API_HOST}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    cache: 'no-store',
    keepalive,
    body: JSON.stringify(body),
  }, TIMEOUT_MS)
  const envelope = (await response.json()) as ApiEnvelope<T>
  if (!response.ok || envelope.status !== 'success') {
    throw new Error(envelope?.message ?? `chat api ${response.status}`)
  }
  return envelope.data as T
}

export function fetchChatHistory(credential: Credential): Promise<HistoryResult> {
  return post<HistoryResult>(HISTORY_PATH, credentialBody(credential))
}

/**
 * Persists a batch of the caller's own messages. `keepalive` on the final
 * flush lets the browser finish the request even as the tab closes, so the
 * audit trail does not lose whatever a child typed in their last minute.
 */
export function persistChatMessages(
  credential: Credential,
  messages: Array<{ id: string; body: string; at: number; attachment?: unknown }>,
  options?: { keepalive?: boolean },
): Promise<{ persisted: number }> {
  return post<{ persisted: number }>(
    MESSAGES_PATH,
    { ...credentialBody(credential), messages },
    options?.keepalive ?? false,
  )
}

export type ChatModerationAction = 'freeze' | 'unfreeze' | 'mute' | 'unmute' | 'delete'

/** Returns the full post-action state, including the newly minted rev. */
export function moderateChat(
  credential: Credential,
  action: ChatModerationAction,
  options?: { targetIdentity?: string; messageId?: string },
): Promise<ChatModerationState> {
  return post<ChatModerationState>(MODERATION_PATH, {
    ...credentialBody(credential),
    moderation_action: action,
    target_identity: options?.targetIdentity,
    message_id: options?.messageId,
  })
}

const UNFURL_PATH = '/api/v1/public/livekit/chat_unfurl'

/** What the server extracted from a link — advisory, null when it could not. */
export interface LinkPreview {
  title: string
  description?: string | null
  site?: string | null
  image?: string | null
}

/**
 * Server-side OpenGraph unfurl (the browser cannot: CORS). The server owns
 * the SSRF story; a null preview is a normal answer, never an error. Results
 * are cached server-side per URL — twenty children previewing the tutor's
 * link cost one origin fetch.
 */
export function fetchUnfurl(credential: Credential, url: string): Promise<LinkPreview | null> {
  return post<{ preview: LinkPreview | null }>(UNFURL_PATH, {
    ...credentialBody(credential),
    url,
  }).then((data) => data.preview ?? null)
}

const UPLOADS_PATH = '/api/v1/public/livekit/chat_uploads'

export interface UploadTicket {
  key: string
  put_url: string
  thumb_key: string
  thumb_put_url: string
  public_base: string
}

/** Asks the server for presigned PUTs sized to this exact image + thumbnail. */
export function requestUpload(
  credential: Credential,
  meta: { bytes: number; thumb_bytes: number; width: number; height: number },
): Promise<UploadTicket> {
  return post<UploadTicket>(UPLOADS_PATH, { ...credentialBody(credential), ...meta })
}

/**
 * PUTs a blob straight to S3 with the presigned URL. The content-type MUST
 * match what the presign was signed for or S3 refuses — images are presigned
 * as image/jpeg (the pipeline re-encodes), files as their allowlisted MIME
 * (LK-009: application/pdf, raw bytes, no canvas re-encode). Direct to the
 * bucket — this byte stream never touches our API.
 */
export async function putToS3(url: string, blob: Blob, contentType = 'image/jpeg'): Promise<void> {
  const response = await fetchWithTimeout(
    url,
    { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob },
    30_000,
  )
  if (!response.ok) throw new Error(`upload failed: ${response.status}`)
}

/** The file half of the presign response (LK-009): one object, no thumbnail. */
export interface FileUploadTicket {
  key: string
  put_url: string
}

/**
 * Asks the server for a presigned PUT for a non-image file (LK-009). Same
 * endpoint as images — the API branches on the file-shaped body — and the
 * server owns the key namespace (`…/files/{ulid}.pdf`) and the per-type size
 * ceiling; the client sends exactly what it will PUT. Raw bytes upstream:
 * nothing about a PDF touches the image pipeline.
 */
export function requestFileUpload(
  credential: Credential,
  meta: { bytes: number; content_type: string },
): Promise<FileUploadTicket> {
  return post<FileUploadTicket>(UPLOADS_PATH, { ...credentialBody(credential), ...meta })
}

const FILE_URL_PATH = '/api/v1/public/livekit/chat_file_url'

/**
 * A short-lived PRIVATE presigned GET for a file attachment message (LK-009).
 * The object is not publicly readable — every open goes through the API,
 * which mints the URL for participants of the run only. Credentials ride the
 * body like every other endpoint in this family (one-time codes never belong
 * in a query string), keyed by the message id.
 */
export function requestFileUrl(credential: Credential, messageId: string): Promise<string> {
  return post<{ url: string }>(FILE_URL_PATH, {
    ...credentialBody(credential),
    message_id: messageId,
  }).then((data) => data.url)
}
